import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartTooltip, KpiGrid, LEGEND, Panel, Section, signedPct } from '@/components/qxo/qxoBoardPrimitives';
import { timeAxisProps, useQxoFocus } from '@/components/qxo/qxoFocus';
import {
  LONG_BRANCH_COST,
  LONG_CONSTRUCTION_FUNNEL,
  LONG_CONSTRUCTION_LABOR,
  LONG_EXISTING_HOME_SALES,
  LONG_HOUSING_BACKLOG,
  LONG_HOUSING_STARTS,
  LONG_INPUT_PPI,
  LONG_MORTGAGE,
  LONG_NEW_HOME_MOS,
  LONG_PUT_IN_PLACE,
  LONG_REGIONAL_STARTS,
  LONG_SF_FUNNEL,
  LONG_STARTS_VS_SALES,
  LONG_STRUCTURE_STARTS,
} from '@/components/qxo/qxoLongHistory';
import {
  ARMA_LATEST,
  ARMA_QUARTERLY,
  BLDR_CORE_ORGANIC,
  DATA_QUALITY,
  EXISTING_HOME_SALES,
  EXISTING_HOME_SALES_ANNUAL,
  HAIL_SPC_ONE_INCH,
  HOUSING_LATEST,
  HOUSING_STARTS_Q,
  IBP_CONSOL_SB,
  IBP_RESI_SB,
  KPIS,
  MILL_VS_BRANCH,
  MORTGAGE_LATEST,
  MORTGAGE_Q,
  OC_CHANNEL,
  OC_ROOFING,
  QXO_Q2_SSS,
  ROOFING_DASHBOARD_AS_OF,
  WATCHLIST,
} from '@/components/qxo/qxoRoofingCharts';
import {
  BACKLOG_LATEST,
  BRANCH_COST,
  BRANCH_COST_LATEST,
  CONSTRUCTION_LABOR,
  DEEP_KPIS,
  HOUSING_BACKLOG,
  LABOR_LATEST,
  LIRA,
  NEW_HOME_LATEST,
  NEW_HOME_MOS,
  PUT_IN_PLACE,
  PUT_IN_PLACE_LATEST,
  SF_FUNNEL,
  SF_FUNNEL_LATEST,
  STARTS_VS_SALES,
  STRUCTURE_LATEST,
  STRUCTURE_STARTS,
} from '@/components/qxo/qxoRoofingDeep';
import {
  AIA_ABI,
  ARMA_STEEP_VS_LOWSLOPE,
  BLDR_END_MARKET,
  CONSTRUCTION_FUNNEL,
  FUNNEL_LATEST,
  IBP_INSTALL_SPLIT,
  INPUT_PPI,
  PPI_LATEST,
  REGIONAL_STARTS,
  REPORTED_MIX,
  STACK_KPIS,
  TOPBUILD_FY25_SB,
  TOPBUILD_SB,
} from '@/components/qxo/qxoRoofingStack';

const PEER_TAPE = IBP_RESI_SB.map((row) => {
  const bldr = BLDR_CORE_ORGANIC.find((b) => b.period === row.period);
  const ibpAll = IBP_CONSOL_SB.find((b) => b.period === row.period);
  return {
    period: row.period,
    ibpResi: row.yoy,
    ibpConsol: ibpAll?.yoy ?? null,
    bldrCore: bldr?.yoy ?? null,
  };
});

const INSTALLER_SPLIT = IBP_INSTALL_SPLIT.map((row) => {
  const tb = TOPBUILD_SB.find((t) => t.period === row.period);
  return {
    period: row.period,
    ibpResi: row.resi,
    ibpComm: row.comm,
    topbuildResi: tb?.resi ?? null,
  };
});

const MIX_CHART = REPORTED_MIX.map((row) => ({
  ...row,
  period:
    row.period === 'Q2 24 Beacon'
      ? 'Beacon Q2 24'
      : row.period === 'Q2 25 QXO'
        ? 'QXO Q2 25'
        : 'QXO + Kodiak Q2 26',
}));

export function QxoRoofingMarketDashboard() {
  const { focusId } = useQxoFocus();
  return (
    <section className={focusId ? 'w-full min-w-0 text-white' : 'w-full min-w-0 max-w-full overflow-hidden rounded-2xl bg-[#071f38] text-white'}>
      {!focusId ? <header className="flex flex-wrap items-start justify-between gap-3 border-b border-sky-900/80 px-5 py-4">
        <div>
          <p className="text-lg font-semibold tracking-tight">
            QXO <span className="font-normal text-sky-200">Roofing Market Dashboard</span>
          </p>
          <p className="mt-1 max-w-3xl text-[13px] leading-5 text-sky-200">
            Read top to bottom: what mills shipped, what housing is doing, then the pipeline that feeds distributors
            and installers. A star on a quarter (Q3 26*) means the quarter is not finished yet. Empty gaps are missing
            prints, not zeros.
          </p>
        </div>
        <div className="text-right text-[11px] text-sky-300">
          <p>{ROOFING_DASHBOARD_AS_OF}</p>
          <p>Public filings, Census, BLS, ARMA</p>
        </div>
      </header> : null}

      <KpiGrid items={KPIS} />

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="arma-shipments"
          tall
          title="Shingles leaving U.S. mills"
          unit="Million squares per quarter"
          takeaway={`Q2 2026 was ${ARMA_LATEST.q2Yoy > 0 ? '+' : ''}${ARMA_LATEST.q2Yoy}% vs last year (${(ARMA_LATEST.q2Squares / 1e6).toFixed(1)}M squares) after a −9.9% Q1. First half is still ${ARMA_LATEST.h1Yoy}%.`}
          note="This is factory shipments, including restock into yards — not what contractors bought. Green line is the change vs the same quarter last year. Q4 2023–24 are inferred from the 2024 year-end total; Q4 2025 is missing."
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={ARMA_QUARTERLY} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 1)} />
              <YAxis yAxisId="sq" tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={[24, 56]} />
              <YAxis yAxisId="yoy" orientation="right" tick={{ fill: '#86efac', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip
                content={
                  <ChartTooltip
                    formatValue={(value, name) => (name.includes('vs') ? signedPct(value) : `${value.toFixed(1)}M sq`)}
                  />
                }
              />
              <Bar yAxisId="sq" dataKey="squaresM" name="Squares shipped" fill="#38bdf8" radius={[3, 3, 0, 0]} />
              <Line
                yAxisId="yoy"
                type="monotone"
                dataKey="yoy"
                name="vs same quarter last year"
                stroke="#86efac"
                strokeWidth={2}
                connectNulls={false}
                dot={{ r: 3, fill: '#86efac' }}
              />
              <Legend wrapperStyle={LEGEND} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="mills-vs-branch"
          tall
          title="Mills vs QXO store sales"
          unit="Percent vs the same quarter last year"
          takeaway={`Mills were roughly flat in Q2. QXO’s steep-slope same-store sales were ${signedPct(QXO_Q2_SSS.steep)} (volume ${signedPct(QXO_Q2_SSS.steepVolume)}, price ${signedPct(QXO_Q2_SSS.steepPrice)}). That gap is destocking or lost share — not a mill miss.`}
          note="The pink and gold marks are single prints, not a trend. QXO same-store is a trade-press quote of a company official, not the 10-Q. Beacon’s 2024 gold dot includes new branches."
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={MILL_VS_BRANCH} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 1)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip content={<ChartTooltip formatValue={(value) => signedPct(value)} />} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Line
                type="monotone"
                dataKey="armaYoy"
                name="Mill shipments"
                stroke="#38bdf8"
                strokeWidth={2}
                dot={{ r: 2, fill: '#38bdf8' }}
              />
              <Line
                type="monotone"
                dataKey="beaconOrganic"
                name="Beacon organic (one print)"
                stroke="#fbbf24"
                strokeWidth={2}
                connectNulls={false}
                dot={{ r: 5, fill: '#fbbf24' }}
              />
              <Line
                type="monotone"
                dataKey="qxoSteepSss"
                name="QXO steep-slope stores (one print)"
                stroke="#fb7185"
                strokeWidth={2}
                connectNulls={false}
                dot={{ r: 5, fill: '#fb7185' }}
              />
              <Legend wrapperStyle={LEGEND} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="housing-starts"
          tall
          title="How many homes are being started"
          unit="Million homes, annualized — quarterly average of monthly Census prints"
          takeaway={`${HOUSING_LATEST.month}: ${HOUSING_LATEST.totalSaar}M total (${signedPct(HOUSING_LATEST.yoyTotal)} vs last year), ${HOUSING_LATEST.singleSaar}M of that is houses. This is new construction, not reroof.`}
          note="TopBuild and Kodiak feel this more than Beacon’s repair book. Q3 26* is July–August only."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_HOUSING_STARTS : HOUSING_STARTS_Q} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [0.7, 1.8]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(2)}M / year`} />} />
              <Line type="monotone" dataKey="total" name="All homes" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="single" name="Houses only" stroke="#fbbf24" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="mortgage-rate"
          tall
          title="30-year mortgage rate"
          unit="Percent — quarterly average of weekly Freddie Mac prints"
          takeaway={`Latest week ${MORTGAGE_LATEST.date}: ${MORTGAGE_LATEST.rate}%. High rates slow both new houses and discretionary reroofs.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_MORTGAGE : MORTGAGE_Q} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [2.5, 8]} unit="%" />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(2)}%`} />} />
              <Line type="monotone" dataKey="rate" name="30-year fixed" stroke="#fbbf24" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="existing-home-sales"
          title="Existing homes changing hands"
          unit="Million homes sold per year"
          takeaway={`${EXISTING_HOME_SALES.month} annualized pace: ${EXISTING_HOME_SALES.saarM}M (${signedPct(EXISTING_HOME_SALES.yoy)} vs last year). Inventory ${EXISTING_HOME_SALES.inventoryM}M, or ${EXISTING_HOME_SALES.monthsSupply} months of supply.`}
          note="Turnover is a weak reroof proxy. Roof age and storms matter more. The bars are calendar years; the takeaway is the latest month."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={focusId ? LONG_EXISTING_HOME_SALES : EXISTING_HOME_SALES_ANNUAL} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="year" {...timeAxisProps(focusId != null, 0)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [3.5, 6.5]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value}M sold`} />} />
              <Bar dataKey="salesM" name="Annual sales" fill="#38bdf8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="oc-roofing"
          title="Owens Corning roofing (largest branded mill)"
          unit="Net sales, $ millions"
          takeaway={`Q2 sales dollars were up a little; volume was about ${OC_CHANNEL.q2VolYoy}%. First-half volume ${OC_CHANNEL.h1VolYoy}%. ${OC_CHANNEL.stormCarryover}.`}
          note="The red line is sales dollars vs last year, which mixes price and volume. Volume is the closer cousin to ARMA squares."
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={OC_ROOFING} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 0)} />
              <YAxis yAxisId="s" tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="y" orientation="right" tick={{ fill: '#fb7185', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip
                content={
                  <ChartTooltip
                    formatValue={(value, name) => (name.includes('vs') ? signedPct(value) : `$${value}M`)}
                  />
                }
              />
              <Bar yAxisId="s" dataKey="salesM" name="Net sales" fill="#38bdf8" radius={[3, 3, 0, 0]} />
              <Line
                yAxisId="y"
                type="monotone"
                dataKey="salesYoy"
                name="Sales $ vs last year"
                stroke="#fb7185"
                strokeWidth={2}
                connectNulls={false}
              />
              <Legend wrapperStyle={LEGEND} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="peer-tape"
          tall
          title="Installer and lumber peers"
          unit="Percent vs last year — each company’s own organic / same-branch definition"
          takeaway="IBP residential jobs (pink) are the public stand-in for TopBuild until QXO reports install organic. BLDR (gold dots) is the stand-in for Kodiak lumber. BLDR’s Q2 repair book was only −1.8% — firmer than new construction."
          note="Do not average these into one “organic.” Blank gold points are quarters we have not verified."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={PEER_TAPE} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 0)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip content={<ChartTooltip formatValue={(value) => signedPct(value)} />} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Line type="monotone" dataKey="ibpResi" name="IBP residential jobs" stroke="#fb7185" strokeWidth={2} />
              <Line type="monotone" dataKey="ibpConsol" name="IBP whole company" stroke="#94a3b8" strokeWidth={2} />
              <Line
                type="monotone"
                dataKey="bldrCore"
                name="Builders FirstSource (Kodiak analog)"
                stroke="#fbbf24"
                strokeWidth={2}
                connectNulls={false}
                dot={{ r: 5, fill: '#fbbf24' }}
              />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="oc-channel"
          title="What Owens Corning said about the channel"
          unit="Call commentary · hail events are a count, not squares of shingles"
          takeaway="Midwest held up; Southwest and Southeast were storm-dependent. Distributor yards a little heavier than normal. Hail in 2024–25 was almost the same — it does not explain the mill slowdown."
        >
          <div className="grid h-full grid-cols-1 gap-3 sm:grid-cols-2">
            <ul className="space-y-2 text-[12px] leading-5 text-sky-100">
              <li>
                Stronger: <span className="text-emerald-300">{OC_CHANNEL.regionsGood}</span>
              </li>
              <li>
                Weaker: <span className="text-rose-300">{OC_CHANNEL.regionsWeak}</span>
              </li>
              <li>{OC_CHANNEL.inventory}</li>
            </ul>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={HAIL_SPC_ONE_INCH} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke="#1e4976" vertical={false} />
                <XAxis dataKey="year" {...timeAxisProps(focusId != null, 0)} />
                <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 6000]} />
                <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toLocaleString()} storms`} />} />
                <Bar dataKey="events" name="1-inch hail storms" fill="#38bdf8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <Section title="The housing pipeline">
        Permits become starts, starts become completions months later. Completions are what installer crews feel.
        All three lines are the same Census survey, annualized, in millions of homes.
      </Section>

      <KpiGrid items={STACK_KPIS} />

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="construction-funnel"
          tall
          title="Permits, then starts, then finished homes"
          unit="Million homes per year, annualized"
          takeaway={`${FUNNEL_LATEST.month}: permits ${FUNNEL_LATEST.permit}M (${signedPct(FUNNEL_LATEST.permitYoy)}), completions ${FUNNEL_LATEST.complete}M (${signedPct(FUNNEL_LATEST.completeYoy)}). Finished houses were ${FUNNEL_LATEST.sfComplete}M. Completions have rolled over hard — that hits jobsites after starts already did.`}
          note="Q3 26* is July–August only."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_CONSTRUCTION_FUNNEL : CONSTRUCTION_FUNNEL} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [1.0, 2.0]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(2)}M / year`} />} />
              <Line type="monotone" dataKey="permit" name="Permits" stroke="#94a3b8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="starts" name="Starts" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="complete" name="Finished homes" stroke="#86efac" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="regional-starts"
          tall
          title="Where homes are being started"
          unit="Million homes per year, annualized"
          takeaway="The South is the storm / Sunbelt roof book. The Midwest is the pocket Owens Corning called strong in Q2. These are new houses, not shingle squares."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_REGIONAL_STARTS : REGIONAL_STARTS} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [0, 1.0]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(2)}M / year`} />} />
              <Line type="monotone" dataKey="south" name="South" stroke="#fb7185" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="midwest" name="Midwest" stroke="#86efac" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="west" name="West" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="ne" name="Northeast" stroke="#94a3b8" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="steep-vs-low"
          title="Steep-slope vs low-slope mill shipments"
          unit="Percent vs the same quarter last year — both from the same ARMA report"
          takeaway="Q2 2026: shingles were flat; modified bitumen (flat / commercial roofs) was +11.4%. That is not Beacon’s steep-slope store number."
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={ARMA_STEEP_VS_LOWSLOPE} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 0)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip content={<ChartTooltip formatValue={(value) => signedPct(value)} />} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Bar dataKey="shingles" name="Shingles (houses)" fill="#38bdf8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="modbit" name="Mod-bit (flat roofs)" fill="#fbbf24" radius={[3, 3, 0, 0]} />
              <Legend wrapperStyle={LEGEND} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="input-ppi"
          title="What mills pay vs what they charge vs lumber"
          unit="Price index, 2019 Q1 = 100"
          takeaway={`${PPI_LATEST.month}: asphalt (the feedstock) ${signedPct(PPI_LATEST.asphaltYoy)} vs last year; mill shingles ${signedPct(PPI_LATEST.shingleYoy)}; lumber ${signedPct(PPI_LATEST.lumberYoy)}. The asphalt spike is why Owens Corning talked about a negative price/cost spread.`}
          note="These are not dollar prices. All three series share an index so you can see which one ran away."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_INPUT_PPI : INPUT_PPI} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [90, 220]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(0)} (2019 Q1=100)`} />} />
              <Line type="monotone" dataKey="asphalt" name="Asphalt (feedstock)" stroke="#fb7185" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="shingles" name="Shingles (mill selling price)" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="lumber" name="Lumber (Kodiak book)" stroke="#fbbf24" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="installer-split"
          tall
          title="Installer jobs: houses vs commercial"
          unit="Same-branch sales vs last year"
          takeaway={`IBP’s house jobs have been negative; commercial has been the offset. TopBuild’s own full-year 2025 split: residential ${signedPct(TOPBUILD_FY25_SB.resi)}, commercial ${signedPct(TOPBUILD_FY25_SB.ci)}. Architecture billings in ${AIA_ABI.month} were ${AIA_ABI.score} — below 50 means design firms are contracting.`}
          note="Gold dots are the two TopBuild quarters that published a residential split. Architecture billings are a pipeline read, not square footage."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={INSTALLER_SPLIT} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 0)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip content={<ChartTooltip formatValue={(value) => signedPct(value)} />} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Line type="monotone" dataKey="ibpResi" name="IBP houses" stroke="#fb7185" strokeWidth={2} />
              <Line type="monotone" dataKey="ibpComm" name="IBP commercial" stroke="#86efac" strokeWidth={2} />
              <Line
                type="monotone"
                dataKey="topbuildResi"
                name="TopBuild houses (sparse)"
                stroke="#fbbf24"
                strokeWidth={2}
                connectNulls={false}
                dot={{ r: 5, fill: '#fbbf24' }}
              />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="bldr-end-market"
          tall
          title="Kodiak analog: what end market is weak"
          unit="Builders FirstSource core organic vs last year"
          takeaway="New houses and apartments are the drag. Repair / remodel is the closer cousin to Beacon and has held up better. Q1 2026 company-wide organic was not disclosed — left blank on purpose."
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={BLDR_END_MARKET} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 0)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip content={<ChartTooltip formatValue={(value) => signedPct(value)} />} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Bar dataKey="sf" name="New houses" fill="#fb7185" radius={[3, 3, 0, 0]} />
              <Bar dataKey="rr" name="Repair / other" fill="#38bdf8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="mf" name="Apartments" fill="#94a3b8" radius={[3, 3, 0, 0]} />
              <Legend wrapperStyle={LEGEND} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="reported-mix"
          title="What QXO reports as the sales mix"
          unit="Share of reported net sales — not organic growth"
          takeaway="The jump in complementary in 2026 is Kodiak lumber entering the print, not Beacon taking mix away from roofing."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={MIX_CHART} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 0)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" domain={[0, 100]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value}% of sales`} />} />
              <Bar dataKey="resi" name="Residential roofing" stackId="mix" fill="#38bdf8" />
              <Bar dataKey="nonresi" name="Commercial roofing" stackId="mix" fill="#7dd3fc" />
              <Bar dataKey="complementary" name="Complementary (incl. lumber)" stackId="mix" fill="#fbbf24" />
              <Legend wrapperStyle={LEGEND} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Section title="Under the pipeline">
        {`Houses vs apartments, unfinished homes sitting in the pipeline, dollars of construction work, then what it costs a branch to deliver. Harvard’s remodeling model (${LIRA.vintage}) sees owner-occupied repair slowing to ${signedPct(LIRA.mid2027Yoy)} by mid-2027 on a $${LIRA.mid2027SpendBn}B run-rate — a forecast, so it is not on a Census chart.`}
      </Section>

      <KpiGrid items={DEEP_KPIS} />

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="structure-starts"
          tall
          title="Houses vs apartments"
          unit="Million homes started per year, annualized"
          takeaway={`${STRUCTURE_LATEST.month}: houses ${signedPct(STRUCTURE_LATEST.sfYoy)} at 918k; apartment buildings (5+ units) ${signedPct(STRUCTURE_LATEST.mfYoy)} at 344k. Steep-slope shingles track houses.`}
          note="2–4 unit buildings are about 13k starts and are left off so they do not look like a third product line."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_STRUCTURE_STARTS : STRUCTURE_STARTS} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [0.2, 1.3]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(2)}M / year`} />} />
              <Line type="monotone" dataKey="sf" name="Houses" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="mf" name="Apartments (5+ units)" stroke="#fb7185" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="sf-funnel"
          tall
          title="The same pipeline, houses only"
          unit="Million houses per year, annualized"
          takeaway={`${SF_FUNNEL_LATEST.month}: permits ${SF_FUNNEL_LATEST.permit}M (${signedPct(SF_FUNNEL_LATEST.permitYoy)}), starts ${SF_FUNNEL_LATEST.starts}M (${signedPct(SF_FUNNEL_LATEST.startsYoy)}), finished ${SF_FUNNEL_LATEST.complete}M (${signedPct(SF_FUNNEL_LATEST.completeYoy)}). Finished houses are still the lag that hits jobsites.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_SF_FUNNEL : SF_FUNNEL} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [0.7, 1.25]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(2)}M / year`} />} />
              <Line type="monotone" dataKey="permit" name="Permits" stroke="#94a3b8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="starts" name="Starts" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="complete" name="Finished houses" stroke="#86efac" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="starts-vs-sales"
          title="Houses started vs houses sold"
          unit="Million houses per year, annualized"
          takeaway={`${NEW_HOME_LATEST.month}: builders sold ${NEW_HOME_LATEST.sold}M (${signedPct(NEW_HOME_LATEST.soldYoy)} vs last year). Starts have been running ahead of sales — that is how unsold inventory builds.`}
          note="The gap is builder inventory, not mill shipments."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_STARTS_VS_SALES : STARTS_VS_SALES} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [0.5, 1.3]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(2)}M / year`} />} />
              <Line type="monotone" dataKey="starts" name="Started" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="sold" name="Sold" stroke="#fbbf24" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="new-home-mos"
          title="How long to sell the new-home inventory"
          unit="Months of supply — how long current unsold new houses would last at the current sales pace"
          takeaway={`${NEW_HOME_LATEST.month}: ${NEW_HOME_LATEST.mos} months (${signedPct(NEW_HOME_LATEST.mosYoy)} vs last year). Around 6 months is historically tight. At 9+ months, starts can print “up” vs last year and still not restock distributors.`}
          note="Dashed line is 6 months. This is months, not houses — that is why it is not on the chart next door."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_NEW_HOME_MOS : NEW_HOME_MOS} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [3, 11]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(1)} months`} />} />
              <ReferenceLine y={6} stroke="#64748b" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="mos" name="Months of supply" stroke="#fb7185" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="housing-backlog"
          tall
          title="Homes already in the pipeline"
          unit="Million homes sitting there — a count, not an annualized rate"
          takeaway={`${BACKLOG_LATEST.month}: ${BACKLOG_LATEST.under}M under construction (${signedPct(BACKLOG_LATEST.underYoy)}), ${BACKLOG_LATEST.notStarted}M permitted but not started (${signedPct(BACKLOG_LATEST.notStartedYoy)}). That unfinished pile is what installer crews work through after starts slow.`}
          note="Under construction is seasonally adjusted. Permitted-not-started is not. Both are still a snapshot of homes, not a yearly flow."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_HOUSING_BACKLOG : HOUSING_BACKLOG} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [0, 1.9]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(2)}M homes`} />} />
              <Line type="monotone" dataKey="under" name="Under construction" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="notStarted" name="Permitted, not started" stroke="#fbbf24" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="put-in-place"
          tall
          title="Dollars of construction work being put in place"
          unit="Billions of dollars per year, annualized"
          takeaway={`${PUT_IN_PLACE_LATEST.month}: residential $${PUT_IN_PLACE_LATEST.resi}B (${signedPct(PUT_IN_PLACE_LATEST.resiYoy)}), commercial $${PUT_IN_PLACE_LATEST.nonres}B (${signedPct(PUT_IN_PLACE_LATEST.nonresYoy)}). This is money spent, not a count of roofs.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_PUT_IN_PLACE : PUT_IN_PLACE} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [400, 1000]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `$${value.toFixed(0)}B / year`} />} />
              <Line type="monotone" dataKey="resi" name="Residential" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="nonres" name="Commercial / industrial" stroke="#fb7185" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="branch-cost"
          title="What it costs a branch to move product"
          unit="Price index, 2019 Q1 = 100"
          takeaway={`${BRANCH_COST_LATEST.month}: diesel ${signedPct(BRANCH_COST_LATEST.dieselYoy)} vs last year; truck freight ${signedPct(BRANCH_COST_LATEST.freightYoy)}. Diesel jumps; freight is the slower pass-through into delivery cost.`}
          note="This sits below mill asphalt/shingle prices. Diesel is not seasonally adjusted, so month-to-month noise is real."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_BRANCH_COST : BRANCH_COST} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={focusId ? ['dataMin', 'dataMax'] : [40, 290]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => `${value.toFixed(0)} (2019 Q1=100)`} />} />
              <Line type="monotone" dataKey="diesel" name="Diesel" stroke="#fb7185" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="freight" name="Truck freight" stroke="#fbbf24" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="construction-labor"
          title="Construction payrolls"
          unit="Percent vs the same quarter last year"
          takeaway={`${LABOR_LATEST.empMonth}: ${LABOR_LATEST.emp.toLocaleString()} thousand people on construction payrolls (${signedPct(LABOR_LATEST.empYoy)}). Job openings in ${LABOR_LATEST.openMonth} were ${LABOR_LATEST.open} thousand (${signedPct(LABOR_LATEST.openYoy)}) — too jumpy to plot without flattening this line.`}
          note="Payrolls are the slower read on whether crews are still in the field."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={focusId ? LONG_CONSTRUCTION_LABOR : CONSTRUCTION_LABOR} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" {...timeAxisProps(focusId != null, 2, 3)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" domain={focusId ? ['dataMin', 'dataMax'] : [-10, 10]} />
              <Tooltip content={<ChartTooltip formatValue={(value) => signedPct(value)} />} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Line type="monotone" dataKey="empYoy" name="Payrolls vs last year" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {!focusId ? <div className="grid gap-3 px-4 pb-4 lg:grid-cols-2">
        <article className="rounded-xl border border-sky-900/60 bg-[#0d3358] p-4">
          <h3 className="text-[14px] font-semibold text-white">What to watch next</h3>
          <p className="mt-1 text-[12px] text-sky-300">Prints that would change a Beacon / TopBuild / Kodiak sum-of-parts.</p>
          <ul className="mt-3 space-y-3">
            {WATCHLIST.map((row) => (
              <li key={row.item} className="border-t border-sky-900/70 pt-3 first:border-0 first:pt-0">
                <p className="text-[13px] font-medium text-white">{row.item}</p>
                <p className="mt-1 text-[12px] leading-5 text-sky-200">{row.why}</p>
                <p className="mt-1 text-[11px] text-sky-400">{row.next}</p>
              </li>
            ))}
          </ul>
        </article>
        <article className="rounded-xl border border-sky-900/60 bg-[#0d3358] p-4">
          <h3 className="text-[14px] font-semibold text-white">How to read the data</h3>
          <p className="mt-1 text-[12px] text-sky-300">If it is not a filing or a statistical agency, it is labeled.</p>
          <ul className="mt-3 space-y-3">
            {DATA_QUALITY.map((row) => (
              <li key={row.series} className="border-t border-sky-900/70 pt-3 first:border-0 first:pt-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-[13px] font-medium text-white">{row.series}</p>
                  <span className="shrink-0 rounded-md bg-[#0a2744] px-2 py-0.5 text-[10px] uppercase tracking-wide text-sky-300">
                    {row.grade}
                  </span>
                </div>
                <p className="mt-1 text-[12px] leading-5 text-sky-200">{row.note}</p>
              </li>
            ))}
          </ul>
        </article>
      </div> : null}

      {!focusId ? <footer className="border-t border-sky-900/80 px-5 py-3 text-[11px] leading-4 text-sky-400">
        Sources: ARMA mill shipments; U.S. Census housing and construction spending; BLS prices, payrolls, and job
        openings; Freddie Mac mortgage rates; NAR existing-home sales; Owens Corning, IBP, TopBuild, and Builders
        FirstSource filings; AIA architecture billings; Harvard remodeling outlook (July 2026); Craig Webb (17 Aug
        2026) for QXO same-store. Series IDs live in docs/QXO_ROOFING_DASHBOARD.md.
      </footer> : null}
    </section>
  );
}
